import ExclamationTriangle from "@/components/core/badges/ExclamationTriangle";
import { getJseAuthCookie } from "@/utils/helpers/api.helper";
import BasicButton from "@components/core/buttons/BasicButton";
import BasicModal from "@components/core/modals/BasicModal";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import { ROUTES } from "@routes/Routes";
import {
  setOnboardingError,
  setOnboardingToFalse,
} from "@state/auth/AuthEvents";
import {
  requestOnboardingLogout,
  requestRevokeCertificateForRenewNow,
} from "@state/onboarding/OnboardingEffects";
import { CERTIFICATE_REVOKE_REASON } from "@utils/Constant";
import DateFormat from "@utils/DateFormat";
import { toastError } from "@utils/helpers/toast-helper";
import parse from "html-react-parser";
import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface OBUsSoonExpirationPageProps {
  usExpirationDate: Date;
  certificateDegradedMode: boolean;
  setLoading: (loading: boolean) => void;
  isInformationModalOpen: boolean;
  setIsInformationModalOpen: Dispatch<SetStateAction<boolean>>;
  isRenewUsCertificateModalOpen: boolean;
  setIsRenewUsCertificateModalOpen: Dispatch<SetStateAction<boolean>>;
  isReconnectionNeededModalOpen: boolean;
  setIsReconnectionNeededModalOpen: Dispatch<SetStateAction<boolean>>;
  className?: string;
}

const OBUsSoonExpirationPage: FunctionComponent<
  OBUsSoonExpirationPageProps
> = ({
  usExpirationDate,
  certificateDegradedMode,
  setLoading,
  isInformationModalOpen,
  setIsInformationModalOpen,
  isRenewUsCertificateModalOpen,
  setIsRenewUsCertificateModalOpen,
  isReconnectionNeededModalOpen,
  setIsReconnectionNeededModalOpen,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleConfirm = (): void => {
    setLoading(true);
    void requestRevokeCertificateForRenewNow({
      dto: {
        status: CERTIFICATE_REVOKE_REASON.CERTIFICATE_REVOKED_RENEW,
      },
    })
      .then((res) => {
        if (res.ok) {
          res.data?.onboardingError &&
            setOnboardingError(res.data?.onboardingError);
          navigate(ROUTES.extranet.home.generate());
        } else {
          toastError(t("onboarding.renewUsCertificate.modal.revokeError"));
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <div
      className={`app-onboarding onboarding-us-soon-expiration ${className}`}
    >
      <h3 className="mb-16">{t("onboarding.renewUsCertificate.title")}</h3>
      <p>
        {t("onboarding.renewUsCertificate.message", {
          date: DateFormat.certificateDate(usExpirationDate),
        })}
      </p>
      <div className="d-flex align-items-center gap-8">
        <BasicButton
          variant="secondary"
          text={t("onboarding.renewUsCertificate.modal.noActionLabel")}
          onClick={(e) => {
            e.preventDefault();
            setOnboardingToFalse();
            setIsInformationModalOpen(true);
          }}
        />
        <BasicButton
          disabled={certificateDegradedMode}
          variant="primary"
          text={t("onboarding.renewUsCertificate.renewMyCertificate")}
          onClick={(e) => {
            e.preventDefault();
            const authCookie = getJseAuthCookie();
            if (!certificateDegradedMode && !!authCookie) {
              setIsRenewUsCertificateModalOpen(true);
            }
            if (!authCookie) {
              setIsReconnectionNeededModalOpen(true);
            }
          }}
        />
      </div>
      {certificateDegradedMode && (
        <ExclamationTriangle
          text={t("account.mySignature.certificate.unavailable")}
          className="mt-24"
        />
      )}
      <ConfirmModal
        visible={isRenewUsCertificateModalOpen}
        onCancel={() => {
          setIsRenewUsCertificateModalOpen(false);
          setOnboardingToFalse();
          setIsInformationModalOpen(true);
        }}
        onOk={() => handleConfirm()}
        title={t("onboarding.renewUsCertificate.modal.title")}
        content={parse(t("onboarding.renewUsCertificate.modal.body"))}
        okText={t("onboarding.renewUsCertificate.modal.yesActionLabel")}
        cancelText={t("onboarding.renewUsCertificate.modal.noActionLabel")}
      />
      <ConfirmModal
        visible={isReconnectionNeededModalOpen}
        onCancel={() => setIsReconnectionNeededModalOpen(false)}
        onOk={() => {
          void requestOnboardingLogout({});
          setIsReconnectionNeededModalOpen(false);
        }}
        title={t(
          "account.mySignature.certificate.modal.reconnectionNeeded.title",
        )}
        content={parse(
          t("account.mySignature.certificate.modal.reconnectionNeeded.body"),
        )}
        okText={t(
          "account.mySignature.certificate.modal.reconnectionNeeded.yesActionLabel",
        )}
        cancelText={t(
          "account.mySignature.certificate.modal.reconnectionNeeded.noActionLabel",
        )}
      />
      <BasicModal
        visible={isInformationModalOpen}
        onCancel={() => {
          setIsInformationModalOpen(false);
          navigate(ROUTES.extranet.home.generate());
        }}
        closable={false}
        title={t("onboarding.renewUsCertificate.laterModal.title")}
        footer={
          <BasicButton
            variant="primary"
            text={t("modals.info.dismiss")}
            onClick={() => {
              setIsInformationModalOpen(false);
              navigate(ROUTES.extranet.home.generate());
            }}
            className="w-100"
          />
        }
      >
        <div className="text-left mb-3">
          {t("onboarding.renewUsCertificate.laterModal.body")}
        </div>
      </BasicModal>
    </div>
  );
};

export default OBUsSoonExpirationPage;
